// src/ConnectionContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { notification } from "antd"; // Importar notificaciones de Ant Design
import api from "./axiosConfig";

const ConnectionContext = createContext();

export const useConnection = () => useContext(ConnectionContext);

export const ConnectionProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [latency, setLatency] = useState(null);

  // Función para mostrar notificaciones
  const openNotification = (type, message, description) => {
    notification[type]({
      message,
      description,
      placement: "topRight",
    });
  };

  // Función para medir latencia usando el endpoint de Laravel
  const measureLatency = useCallback(async () => {
    try {
      const startTime = Date.now();

      // Llamar al endpoint de ping súper básico de Laravel con timeout corto
      const response = await api.get("/ping/super-basic", {
        timeout: 5000 // 5 segundos máximo para el ping
      });

      if (response.data.pong) {
        const endTime = Date.now();
        const measuredLatency = endTime - startTime;
        setLatency(measuredLatency);
      } else {
        setLatency(null);
      }
    } catch (error) {
      // Solo mostrar error si no es un timeout esperado o un error de red conocido
      if (error.code !== 'ECONNABORTED' && error.code !== 'ERR_NETWORK') {
        console.error("Error measuring latency:", error);
      }
      
      // Si es un error de red, establecer una latencia alta para indicar problemas
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        setLatency(9999); // Indicador de problemas de red
      } else {
        setLatency(null);
      }
    }
  }, []);

  // Inicializar medición de latencia
  useEffect(() => {
    // Medir latencia inicial
    setTimeout(() => {
      measureLatency();
    }, 1000);

    // Medir latencia cada 60 segundos (reducir frecuencia)
    const latencyInterval = setInterval(() => {
      measureLatency();
    }, 60000);

    return () => {
      clearInterval(latencyInterval);
    };
  }, [measureLatency]);

  // Manejar cambios en la conexión de Internet
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      openNotification(
        "success",
        "Conexión Restablecida",
        "La conexión a Internet ha sido restaurada."
      );
      // Medir latencia cuando se restaura la conexión
      setTimeout(measureLatency, 1000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setLatency(null);
      openNotification(
        "error",
        "Sin Conexión",
        "Se ha perdido la conexión a Internet."
      );
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Limpieza
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [measureLatency]);


  const contextValue = useMemo(
    () => ({ isOnline, latency }),
    [isOnline, latency]
  );

  return (
    <ConnectionContext.Provider value={contextValue}>
      {children}
    </ConnectionContext.Provider>
  );
};

ConnectionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
